import type { IExtEquipmentBooking } from "@agrigate/equipment-api-def/lib/services/EquipmentBookingService/dto";
import useAsyncTask from "@agrigate/shared/lib/hooks/use-async-task";
import FlexContainer from "@agrigate/shared-web-ui/lib/FlexContainer";
import LoopFallBackState from "@agrigate/shared-web-ui/lib/LoopFallBack";
import { bisectBy } from "@agrigate/ts-utils/lib/collections";
import { formatDate } from "@agrigate/ts-utils/lib/datetime";
import {
    Avatar,
    Badge,
    Card,
    Center,
    Container,
    Flex,
    For,
    HStack,
    Icon,
    RadioCard,
    Spacer,
    Text,
} from "@chakra-ui/react";
import { type ReactElement, useState } from "react";
import { BsArrowRight } from "react-icons/bs";
import { MdCheckCircle } from "react-icons/md";
import { useNavigate } from "react-router";
import NarrowLayout from "../components/NarrowLayout";
import rpc from "../utils/rpc";

export default function HomePage(): ReactElement {
    const navigate = useNavigate();

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);

    const [selectedOrderStatus, setSelectedOrderStatus] =
        useState<OrderTab>("pending");

    const fetchBookingsListT = useAsyncTask(
        async () => {
            const data = await rpc.equipment.booking.findByOperator({
                filters: {
                    status: {
                        in: [
                            "CONFIRMED",
                            "COMPLETED",
                            "IN_PROGRESS",
                            "PENDING_PAYMENT",
                        ],
                    },
                },
                enrichments: {
                    booker: true,
                    plots: true,
                    slots: true,
                    model: true,
                    machine: true,
                    pricingConfig: true,
                },
            });
            return data.bookings;
        },
        {
            deps: [],
        },
    );

    const bookings = fetchBookingsListT.data;

    const [pendingBookings, completedBookings] = bisectBy(
        bookings ?? [],
        _ => _.status === "COMPLETED",
    );

    const todayOrders = pendingBookings?.filter(booking => {
        const start = booking.slots?.values?.[0]?.startTime;
        if (!start) return false;
        return start <= endOfToday.getTime();
    });

    const futureOrders = pendingBookings
        ?.filter(booking => {
            const start = booking.slots?.values?.[0]?.startTime;
            return start && start > endOfToday.getTime();
        })
        // Sort ascending
        .sort((a, b) => a.startTime - b.startTime);

    const renderBookingCard = (
        booking: IExtEquipmentBooking,
        bookingStatus: OrderTab,
    ): ReactElement => {
        const scheduleDate = formatDate(booking.slots?.values?.[0]?.startTime);
        const scheduleTime = formatDate(
            booking.slots?.values?.[0]?.startTime,
            "hh:mm",
        );
        const scheduleMeridiem = formatDate(
            booking.slots?.values?.[0]?.startTime,
            "a",
        );

        return (
            <Card.Root
                py={2}
                px={4}
                key={booking.id}
                flexDir="row"
                gap="20px"
                alignItems="center"
            >
                <Flex flexDir="column" py="10px" gap={2}>
                    <Avatar.Root
                        h="64px"
                        w="64px"
                        shape="rounded"
                        bgColor="green.50"
                        color="green.700"
                    >
                        <Center flexDir="column">
                            {bookingStatus === "completed" ? (
                                <Icon size={"lg"} color={"brand.500"}>
                                    <MdCheckCircle />
                                </Icon>
                            ) : (
                                <>
                                    <Text fontSize="md" fontWeight="bold">
                                        {scheduleTime}
                                    </Text>
                                    <Text fontSize="sm">
                                        {scheduleMeridiem}
                                    </Text>
                                </>
                            )}
                        </Center>
                    </Avatar.Root>
                    <Text fontSize="xs" color="green.700">
                        {scheduleDate}
                    </Text>
                </Flex>

                <Flex flexDir="column" py="10px" gap={2}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.500">
                        {booking.pubId}
                    </Text>
                    <Text fontSize="md" fontWeight="bold">
                        {booking.booker?.value?.name}
                    </Text>
                    <Text fontSize="xs">
                        {booking.model?.value?.name ?? "Model unavailable"} -{" "}
                        {booking.machine?.value?.pubId ?? "Machine unavailable"}
                    </Text>
                    <Flex direction="row" gap={2}>
                        <Badge>{booking.status?.replace(/_/g, " ")}</Badge>
                    </Flex>
                </Flex>

                <Spacer />

                {bookingStatus === "pending" ? (
                    <Avatar.Root
                        ml="auto"
                        h="50px"
                        w="50px"
                        shape="rounded"
                        onClick={() => {
                            if (
                                booking.status === "PENDING_CONFIRMATION" ||
                                booking.status === "PENDING_PAYMENT"
                            ) {
                                navigate(
                                    `/booking/payment-confirmation?id=${booking.id}`,
                                );
                                return;
                            }
                            navigate(`/booking?id=${booking.id}`);
                        }}
                    >
                        <BsArrowRight />
                    </Avatar.Root>
                ) : null}
            </Card.Root>
        );
    };

    return (
        <NarrowLayout>
            <FlexContainer
                flexProps={{
                    p: { base: "16px" },
                    px: { md: "10%" },
                    gap: "20px",
                    flex: 1,
                }}
            >
                <Text fontSize={"xl"} fontWeight={"bold"}>
                    Your Orders
                </Text>
                <RadioCard.Root
                    variant={"surface"}
                    value={selectedOrderStatus}
                    onValueChange={e => {
                        setSelectedOrderStatus(e.value as OrderTab);
                    }}
                    w={"fit-content"}
                >
                    <HStack align={"stretch"} gap={"20px"}>
                        {orderTabs.map(item => (
                            <RadioCard.Item
                                borderRadius={"30px"}
                                key={item.value}
                                value={item.value}
                                _checked={{
                                    bg: "brand.500",
                                    color: "white", // Usually good to flip text color for contrast
                                    borderColor: "brand.500",
                                }}
                            >
                                <RadioCard.ItemHiddenInput />
                                <RadioCard.ItemControl>
                                    <RadioCard.ItemText>
                                        {item.title}
                                    </RadioCard.ItemText>
                                </RadioCard.ItemControl>
                            </RadioCard.Item>
                        ))}
                    </HStack>
                </RadioCard.Root>
                {selectedOrderStatus === "pending" ? (
                    <>
                        <Container
                            p={0}
                            display={"flex"}
                            flexDir={"column"}
                            gap={"8px"}
                        >
                            <Text fontSize={"lg"} color={"gray.500"}>
                                Pending Orders for today
                            </Text>
                            <For
                                each={todayOrders}
                                fallback={<LoopFallBackState />}
                            >
                                {booking =>
                                    renderBookingCard(
                                        booking,
                                        selectedOrderStatus,
                                    )
                                }
                            </For>
                        </Container>
                        <Container
                            p={0}
                            display={"flex"}
                            flexDir={"column"}
                            gap={"8px"}
                        >
                            <Text fontSize={"lg"} color={"gray.500"}>
                                Orders for Tomorrow
                            </Text>
                            <For
                                each={futureOrders}
                                fallback={<LoopFallBackState />}
                            >
                                {booking =>
                                    renderBookingCard(
                                        booking,
                                        selectedOrderStatus,
                                    )
                                }
                            </For>
                        </Container>
                    </>
                ) : (
                    <Container
                        p={0}
                        display={"flex"}
                        flexDir={"column"}
                        gap={"8px"}
                    >
                        <Text fontSize={"lg"} color={"gray.500"}>
                            Completed Orders
                        </Text>
                        <For
                            each={completedBookings}
                            fallback={<LoopFallBackState />}
                        >
                            {booking =>
                                renderBookingCard(booking, selectedOrderStatus)
                            }
                        </For>
                    </Container>
                )}
            </FlexContainer>
        </NarrowLayout>
    );
}

type OrderTab = "pending" | "completed";

interface OrderTabOption {
    value: OrderTab;
    title: string;
}

const orderTabs: OrderTabOption[] = [
    { value: "pending", title: "Pending" },
    { value: "completed", title: "Completed" },
];
